/**
 * Showing view popup — History section.
 * Wires section collapse, the AI-events filter toggle, and per-call transcript expand.
 */
export function initHistory(popup) {
  const history = popup.querySelector('.js-showing-view-history');
  if (!history || history.dataset.bound === 'true') {
    return;
  }
  history.dataset.bound = 'true';

  initCollapse(history);
  initAiFilter(history);
  initTranscripts(history);
}

function initCollapse(history) {
  const collapse = history.querySelector('.js-history-collapse');
  const list = history.querySelector('.js-history-list');
  const header = history.querySelector('.js-history-header');
  collapse?.addEventListener('click', () => {
    const scroller = scrollParent(history);
    const anchor = header || collapse;
    const anchorTop = anchor.getBoundingClientRect().top;
    releaseTail(scroller);

    const expanded = collapse.getAttribute('aria-expanded') === 'true';
    collapse.setAttribute('aria-expanded', String(!expanded));
    if (list) {
      list.hidden = expanded;
    }

    keepAnchored(scroller, anchor, anchorTop);
  });
}

/**
 * Puts `anchor` back at the viewport position it had before the toggle.
 *
 * The popup body is the scroll container, so collapsing a tall list can remove
 * more height than is left below the header. The browser then clamps scrollTop
 * to the new maximum and the popup slides down whatever we assign afterwards.
 * Reserving the missing height keeps the wanted position reachable.
 */
function keepAnchored(scroller, anchor, anchorTop) {
  if (!scroller) {
    return;
  }

  const target = scroller.scrollTop + anchor.getBoundingClientRect().top - anchorTop;
  const missing = Math.ceil(target - maxScrollTop(scroller));
  if (missing > 0) {
    reserveTail(scroller, missing);
  }
  scroller.scrollTop = target;
}

const reservedTails = new WeakMap();

function reserveTail(scroller, height) {
  const spacer = document.createElement('div');
  spacer.setAttribute('aria-hidden', 'true');
  spacer.style.height = `${height}px`;
  scroller.append(spacer);

  const drop = () => {
    scroller.removeEventListener('scroll', onScroll);
    reservedTails.delete(scroller);
    spacer.remove();
  };

  // Once the reader is back inside the range the content covers on its own, the
  // reserved block is entirely below the fold and can go without a visible jump.
  const onScroll = () => {
    if (scroller.scrollTop <= maxScrollTop(scroller) - height) {
      drop();
    }
  };

  scroller.addEventListener('scroll', onScroll);
  reservedTails.set(scroller, drop);
}

function releaseTail(scroller) {
  if (scroller) {
    reservedTails.get(scroller)?.();
  }
}

function maxScrollTop(scroller) {
  return scroller.scrollHeight - scroller.clientHeight;
}

function scrollParent(node) {
  let el = node.parentElement;
  while (el) {
    const { overflowY } = window.getComputedStyle(el);
    if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

function initAiFilter(history) {
  const toggle = history.querySelector('.js-history-ai-toggle');
  const items = history.querySelectorAll('.js-history-item');
  const apply = () => {
    const aiOnly = !!toggle?.checked;
    items.forEach((item) => {
      item.hidden = aiOnly && item.dataset.ai !== 'true';
    });
  };
  toggle?.addEventListener('change', apply);
  apply();
}

function initTranscripts(history) {
  history.querySelectorAll('.js-history-transcript').forEach((wrap) => {
    const toggle = wrap.querySelector('.js-history-transcript-toggle');
    const lines = wrap.querySelector('.js-history-transcript-lines');
    const summary = wrap.querySelector('.js-history-transcript-summary');
    toggle?.addEventListener('click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      if (lines) {
        lines.hidden = expanded;
      }
      if (summary) {
        summary.hidden = !expanded;
      }
    });
  });
}
